import { useEffect, useRef, type ReactNode } from 'react';

export interface RevealProps {
  children: ReactNode;
  /** Extra class names merged onto the wrapper. */
  className?: string;
  /** Render delay in ms after entering viewport (used for stagger). */
  delay?: number;
  /** Trigger threshold for IntersectionObserver. */
  threshold?: number;
  /** Render `as` element (default `div`). */
  as?: keyof React.JSX.IntrinsicElements;
}

/**
 * Reveal-on-scroll wrapper (spec §7.3.1.5).
 *
 * Pairs with the `.reveal-on-scroll` / `.revealed` CSS in `globals.css`.
 * Uses IntersectionObserver so we don't block paint with animation work,
 * and respects `prefers-reduced-motion` via the CSS rule.
 */
export function Reveal({
  children,
  className = '',
  delay = 0,
  threshold = 0.15,
  as: Tag = 'div',
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('revealed');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (delay > 0) {
            window.setTimeout(() => el.classList.add('revealed'), delay);
          } else {
            el.classList.add('revealed');
          }
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, threshold]);

  const Component = Tag as 'div';
  return (
    <Component
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`reveal-on-scroll ${className}`.trim()}
    >
      {children}
    </Component>
  );
}
